import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { copyText } from "@/lib/utils";
import { HourglassMedium, Copy, CircleNotch, UserMinus } from "@phosphor-icons/react";

const STYLE = {
  waiting: { bg: "var(--bk-rowan-bg)", color: "var(--bk-accent)" },
  offered: { bg: "#FFF3DC", color: "#B08840" },
  booked: { bg: "var(--bk-success-bg)", color: "var(--bk-success)" },
};

export function WaitlistPanel({ businessId }) {
  const [entries, setEntries] = useState(null);
  const load = () => api.get(`/businesses/${businessId}/wellness/waitlist`).then((r) => setEntries((r.data.entries || []).filter((w) => w.status !== "removed"))).catch(() => setEntries([]));
  useEffect(() => { load(); }, [businessId]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (w) => {
    try {
      const { data } = await api.delete(`/businesses/${businessId}/waitlist/${w.id}`);
      load();
      toast.success(data.rowan_message, { duration: 8000, action: { label: "Undo", onClick: async () => {
        try { await api.post(`/businesses/${businessId}/waitlist/${w.id}/restore`); toast.success(`${w.client_name} is back on the waitlist.`); load(); }
        catch { toast.error("Couldn't restore that entry."); }
      } } });
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove that client."); }
  };

  const copyLink = (w) => {
    const url = `${window.location.origin}${w.claim_path}`;
    copyText(url).then((ok) => (ok ? toast.success("Claim link copied — send it to the client.") : toast.error("Couldn't copy the link.")));
  };

  if (entries === null) return <p className="text-sm inline-flex items-center gap-2" style={{ color: "var(--bk-text-2)" }}><CircleNotch size={16} className="animate-spin" /> Loading waitlist…</p>;
  const waiting = entries.filter((w) => w.status === "waiting").length;
  const offered = entries.filter((w) => w.status === "offered").length;

  return (
    <div className="space-y-4" data-testid="waitlist-panel">
      <div className="bk-card p-4 text-sm" style={{ color: "var(--bk-text-2)" }} data-testid="waitlist-howto">How it works: offers go out automatically when a slot opens (cancellation or new availability). Copy a claim link to send it yourself, or remove someone who no longer needs a spot.</div>
      <div className="grid grid-cols-2 gap-3">
        <div className="bk-card p-4"><p className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--bk-text-2)" }}>Waiting</p><p className="font-display text-2xl font-bold mt-1" style={{ color: "var(--bk-text)" }} data-testid="waitlist-waiting-count">{waiting}</p></div>
        <div className="bk-card p-4"><p className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--bk-text-2)" }}>Offers out</p><p className="font-display text-2xl font-bold mt-1" style={{ color: "var(--bk-text)" }} data-testid="waitlist-offered-count">{offered}</p></div>
      </div>
      {entries.length === 0 ? (
        <div className="bk-card p-10 text-center" data-testid="waitlist-empty"><HourglassMedium size={36} weight="duotone" color="var(--bk-accent)" className="mx-auto" /><p className="font-semibold mt-3" style={{ color: "var(--bk-text)" }}>No one is waiting right now.</p><p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Clients join the waitlist from your booking page when a slot is full. When a booking is cancelled, Rowan automatically emails the next person a claim link.</p></div>
      ) : (
        <div className="space-y-2" data-testid="waitlist-list">
          {entries.map((w, i) => {
            const st = STYLE[w.status] || { bg: "#F3F1EC", color: "var(--bk-text-2)" };
            return (
              <div key={w.id} className="bk-card p-4 flex flex-wrap items-center gap-3" data-testid={`waitlist-row-${i}`}>
                <div className="flex-1 min-w-[200px]"><p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{w.client_name}</p><p className="text-xs" style={{ color: "var(--bk-text-2)" }}>{[w.service_name, w.date, w.client_email].filter(Boolean).join(" · ")}</p></div>
                {w.status === "offered" && w.claim_path && <button onClick={() => copyLink(w)} data-testid={`waitlist-copy-${i}`} className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-1.5 rounded-full border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}><Copy size={12} /> Copy claim link</button>}
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full capitalize" style={{ background: st.bg, color: st.color }}>{w.status}</span>
                {w.status !== "booked" && <button onClick={() => remove(w)} aria-label="Remove from waitlist" data-testid={`waitlist-remove-${i}`} className="p-1.5 rounded-full hover:bg-[#FCEEE8]" style={{ color: "var(--bk-energy)" }}><UserMinus size={16} /></button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
